import type { CityConfig } from "@/pages/city-template/types";

export const beavertonConfig: CityConfig = {
  city: "Beaverton",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "beaverton",
  route: "/cities/beaverton",
  title: "Emergency Passport Beaverton, OR | Fast Passport Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in Beaverton, OR — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Washington County and Portland metro travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Beaverton, Oregon",
  heroSubtitle: "Business trip to Asia announced last-minute? Family vacation to Mexico coming up fast? Fast Passport Center helps Beaverton and Washington County travelers get their passports fast — as fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Beaverton%20Oregon%20suburban%20cityscape%20with%20modern%20office%20buildings%20and%20residential%20neighborhoods%2C%20green%20trees%20and%20landscaped%20parks%2C%20Tualatin%20Hills%20Nature%20Park%20visible%2C%20warm%20golden%20afternoon%20light%2C%20photorealistic%20suburban%20landscape%20photography%2C%20Pacific%20Northwest%20greenery%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=beaverton-hero-01&orientation=landscape",
  heroImageAlt: "Beaverton Oregon suburban cityscape with modern buildings and Pacific Northwest greenery",
  geoPosition: "45.4871;-122.8037",
  geoPlacename: "Beaverton, Oregon",
  lastModified: "2026-05-29",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "300 5th Ave, Suite 600, Seattle, WA 98104",
    distance: "About 175 miles north — 3 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Beaverton", zips: "97003–97078" },
    { name: "Hillsboro", zips: "97123–97124" },
    { name: "Tigard", zips: "97223, 97224" },
    { name: "Tualatin", zips: "97062" },
    { name: "Sherwood", zips: "97140" },
    { name: "Forest Grove", zips: "97116" },
    { name: "Cornelius", zips: "97113" },
    { name: "Aloha", zips: "97003, 97006, 97007, 97008" },
    { name: "Bethany", zips: "97006" },
    { name: "Cedar Hills", zips: "97225" },
    { name: "Cedar Mill", zips: "97229" },
    { name: "Murray Hill", zips: "97229" },
    { name: "Raleigh Hills", zips: "97225" },
    { name: "West Slope", zips: "97225" },
    { name: "Garden Home", zips: "97223" },
    { name: "Oak Hills", zips: "97229" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home or work in Beaverton in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Beaverton", desc: "FedEx and UPS locations throughout Beaverton and Hillsboro offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays — especially important for last-minute business travel needs from Silicon Forest." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Priya S.",
      location: "Beaverton, OR",
      rating: 5,
      text: "My company sent me to a client meeting in Singapore with two weeks notice. My passport had expired. Fast Passport Center got it renewed in 5 days. I shipped everything from the FedEx on SW Beaverton Hillsdale Hwy. Incredible turnaround.",
      service: "Passport Renewal",
    },
    {
      name: "Mike T.",
      location: "Hillsboro, OR",
      rating: 5,
      text: "We needed passports for our twin boys before a family trip to Cancun. Fast Passport Center made the child passport process straightforward — even with the dual-parent consent requirements. Got both passports in 9 days. Worth every penny.",
      service: "Child Passport",
    },
    {
      name: "Angela K.",
      location: "Tigard, OR",
      rating: 5,
      text: "Lost my passport the week before a conference in London. Fast Passport Center walked me through the DS-64 form, I shipped from the UPS on SW Pacific Hwy, and had my replacement in 6 days. I honestly didn't think it was possible.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "beaverton-main",
      name: "Beaverton Main Post Office",
      type: "USPS Passport Acceptance",
      address: "4265 SW 106th Ave",
      city: "Beaverton",
      state: "OR",
      zip: "97005",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm, Sat 9am–1pm",
      distance: "Central Beaverton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Beaverton. Appointment required — book online well in advance. Photo services available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "beaverton-murray",
      name: "Beaverton Murray Station",
      type: "USPS Passport Acceptance",
      address: "15020 SW Weir Rd",
      city: "Beaverton",
      state: "OR",
      zip: "97003",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "West Beaverton — Murray Hill area",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "West Beaverton passport acceptance. Confirm appointment and photo service availability before visiting.",
      icon: "ri-mail-line",
    },
    {
      id: "hillsboro-post",
      name: "Hillsboro Main Post Office",
      type: "USPS Passport Acceptance",
      address: "1185 NE 8th Ave",
      city: "Hillsboro",
      state: "OR",
      zip: "97124",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Hillsboro — about 8 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Hillsboro option for Washington County residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "tigard-post",
      name: "Tigard Post Office",
      type: "USPS Passport Acceptance",
      address: "12025 SW Main St",
      city: "Tigard",
      state: "OR",
      zip: "97223",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Tigard — about 5 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Tigard option for southern Washington County residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "washington-county",
      name: "Washington County Clerk",
      type: "County Passport Acceptance",
      address: "155 N 1st Ave, Suite 130",
      city: "Hillsboro",
      state: "OR",
      zip: "97124",
      phone: "503-846-8791",
      hours: "Mon–Fri 8am–5pm (by appointment)",
      distance: "Hillsboro — Government Center",
      website: "https://www.washingtoncountyor.gov/CountyClerk",
      websiteLabel: "Washington County Clerk",
      notes: "County clerk passport acceptance in Hillsboro. Appointment required — often faster scheduling than USPS. Call ahead to confirm passport services.",
      icon: "ri-government-line",
    },
    {
      id: "tualatin-post",
      name: "Tualatin Post Office",
      type: "USPS Passport Acceptance",
      address: "19250 SW Martinazzi Ave",
      city: "Tualatin",
      state: "OR",
      zip: "97062",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Tualatin — about 7 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Tualatin option for south metro residents. Confirm appointment availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Beaverton before international travel?",
      a: "Yes. Through Fast Passport Center, Beaverton travelers can get an expedited passport in as little as 3–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today — don't wait until the last minute.",
    },
    {
      q: "How fast can I renew my passport in Beaverton?",
      a: "Fast passport renewal in Beaverton is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years. No office visit required for most renewals.",
    },
    {
      q: "What if my passport expires before my business trip to Asia?",
      a: "If your passport expires before a business trip to Asia or any international destination, start immediately. Many Asian countries require 6 months of passport validity. Fast Passport Center can expedite your passport renewal so you don't miss your flight out of PDX.",
    },
    {
      q: "Can I get a same-day passport appointment near Beaverton?",
      a: "Same-day passport appointments at the regional passport agency in Seattle are only available for documented life-or-death emergencies and are not guaranteed. For most urgent passport situations near Beaverton, Fast Passport Center is the faster and more predictable option — no Seattle drive required.",
    },
    {
      q: "Can I expedite a child passport in Beaverton?",
      a: "Yes. Child passport expediting in Beaverton is possible through Fast Passport Center, but child passports (for minors 15 and under) do require an in-person acceptance step with parental consent. We provide a clear checklist and instructions. Once the acceptance step is complete, we handle the expedited processing.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If your passport is lost or stolen before an upcoming trip, act immediately. You will need to complete Form DS-64 to report it lost or stolen and apply for a replacement. Fast Passport Center guides you through this process and can expedite your replacement passport. Don't delay — lost passport replacements take extra steps and lost passports cannot be used even if found later.",
    },
    {
      q: "Does Beaverton have a passport agency?",
      a: "Beaverton, Oregon does not have a regional passport agency. The closest regional agencies are the Seattle Passport Agency in Washington (about 175 miles north) and the San Francisco Passport Agency in California (about 640 miles south). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster and more convenient solution for most Beaverton residents.",
    },
    {
      q: "How long does a standard passport take in Beaverton?",
      a: "If you apply through a standard post office acceptance facility in Beaverton, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner than that, you need expedited processing. Fast Passport Center offers Beaverton travelers processing as fast as 24 hours.",
    },
    {
      q: "Can I get a passport renewal near me in Beaverton without visiting an office?",
      a: "Yes. Most Beaverton-area adults who are renewing a passport can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, complete your documents at home, and ship them overnight from any FedEx or UPS location in Beaverton or Hillsboro.",
    },
    {
      q: "What airports do Beaverton passport holders typically fly from?",
      a: "Portland International Airport (PDX) is the primary airport for Beaverton travelers, about 20 miles east, offering direct international flights to Europe, Asia, and Canada. Hillsboro Airport (HIO) is a regional general aviation airport. If your passport isn't in hand, don't book international travel. Fast Passport Center can help you meet your deadline.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Beaverton", address: "4705 SW Watson Ave, Beaverton, OR 97005", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Beaverton", address: "11425 SW Beaverton Hillsdale Hwy, Beaverton, OR 97005", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Beaverton", address: "4585 SW Watson Ave, Beaverton, OR 97005", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 8 PM" },
    { name: "The UPS Store — Beaverton", address: "12325 SW Horizon Blvd, Beaverton, OR 97007", hours: "Mon–Fri 8 AM – 7 PM" },
    { name: "Walmart — Beaverton", address: "17275 NW Cornell Rd, Beaverton, OR 97006", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Hillsboro", address: "735 SE Baseline St, Hillsboro, OR 97123", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Hillsboro", address: "1945 SE 24th Ave, Hillsboro, OR 97123", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Hillsboro", address: "737 SE Baseline St, Hillsboro, OR 97123", hours: "Mon–Fri 7 AM – 10 PM" },
    { name: "Walmart — Hillsboro", address: "2600 NE Cornell Rd, Hillsboro, OR 97124", hours: "Daily 7 AM – 11 PM" },
    { name: "The UPS Store — Tigard", address: "16155 SW Pacific Hwy, Tigard, OR 97224", hours: "Mon–Fri 8 AM – 7 PM" },
  ],
  dmvLocations: [
    {
      name: "Beaverton DMV Office",
      address: "14075 SW Barrows Rd, Beaverton, OR 97007",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Primary DMV office for Beaverton and western Washington County. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Hillsboro DMV Office",
      address: "5370 W Baseline Rd, Hillsboro, OR 97123",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Hillsboro DMV option for central Washington County residents. Appointment recommended.",
      featured: false,
    },
    {
      name: "Sherwood DMV Office",
      address: "20795 SW Tualatin Valley Hwy, Sherwood, OR 97140",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Sherwood DMV option for southern Washington County residents. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international airport — about 20 miles east",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "PDX is the main airport for Beaverton travelers, offering direct international flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver, and more. Max Red Line connects Beaverton to PDX. The busiest travel seasons are May–August and December–January — plan passport needs well in advance.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "HIO",
      name: "Hillsboro Airport",
      distance: "Regional general aviation — about 10 miles west",
      address: "3355 NE Cornell Rd, Hillsboro, OR 97124",
      note: "Hillsboro Airport is a general aviation facility serving private and corporate aircraft. For commercial international travel, PDX is the appropriate airport.",
      flightUrl: "https://www.portofportland.com/HIO",
      flightLabel: "HIO Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from PDX in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Beaverton and Washington County.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Beaverton and Washington County travelers renew fast — passport expired before business trip is one of our most common urgent requests.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Beaverton", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Beaverton travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Beaverton families — minors under 16 require in-person acceptance and parental consent. We provide clear instructions and checklists.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Beaverton residents — we guide you through the acceptance clerk process and handle expedited processing.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines. Same-day passport help for Beaverton travelers.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all handled with the same expedited courier access.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Beaverton, Oregon travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Beaverton residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Beaverton, OR residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Beaverton travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Beaverton families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving Beaverton, Oregon." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Beaverton travelers often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book your trip, especially for summer and holiday travel from PDX." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and can catch these issues before your documents are submitted." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission, critical for last-minute passport renewal in Beaverton." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. Fast Passport Center tells you exactly what you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of Beaverton runs 6–13 weeks. If your trip to Asia, Europe, or Mexico is in under 10 weeks, standard processing is a gamble. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly — especially for international flights out of PDX. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Beaverton travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Beaverton", description: "Drop off at any FedEx or UPS in Beaverton or Hillsboro — most locations are open evenings. No office visit to our facility required." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (3 hours each way)", fpc: "Ship documents overnight from Beaverton" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Portland", route: "/cities/portland" },
    { name: "Fast passport renewal in Eugene", route: "/cities/eugene" },
    { name: "Rush passport service in Hillsboro", route: "#" },
    { name: "Emergency passport help near Tigard", route: "#" },
    { name: "Fast passport renewal for Washington County travelers", route: "/states/oregon" },
  ],
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Beaverton or Hillsboro — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Beaverton and all of Washington County remotely",
  serviceAreaIntro: "Whether you are in downtown Beaverton, Hillsboro, Tigard, Tualatin, Sherwood, or anywhere in the greater Washington County area, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Washington County zip codes. Anywhere in Beaverton, Hillsboro, Tigard, Tualatin, or surrounding areas — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Beaverton as simple as possible. Most Beaverton clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Beaverton travelers. Fast Passport Center reviews your documents before submission to catch these issues before they become problems.",
  testimonialsIntro: "Real reviews from Beaverton, Hillsboro, and Tigard travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Beaverton and Washington County travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
};